/**
 * Destination Overview Section Component
 * Displays destination statistics in a grid layout
 * Follows Single Responsibility Principle
 */

import React from 'react';
import { motion } from 'framer-motion';
import { spacing } from '@/design-system/utils';
import { SectionHeader } from '@/components/ui/section-header';
import { StatCard } from './StatCard';

export interface StatItem {
  title: string;
  value: string | null;
  icon?: string | React.ReactNode;
}

export interface RawStatItem {
  title: string;
  value: string;
  icon?: string;
  tag?: string;
  subtitle?: string;
}

export interface DestinationOverviewSectionProps {
  items: RawStatItem[];
  className?: string;
}

export const DestinationOverviewSection: React.FC<
  DestinationOverviewSectionProps
> = ({ items, className }) => {
  // Define preferred order for known items (matching Figma design)
  // Items not in this list will be appended at the end
  const preferredOrder = [
    'population',
    'weather',
    'timezone',
    'sunrise',
    'sunset',
    'air pressure',
    'currency',
    'humidity',
  ];

  // Filter out items with no valid data (null, undefined, empty, whitespace, or placeholder text)
  const itemsWithData = items.filter(item => {
    if (!item.value || typeof item.value !== 'string') {
      return false;
    }

    const trimmedValue = item.value.trim();
    const itemTitle = item.title.toLowerCase();

    // Check if value is empty or whitespace
    if (trimmedValue.length === 0) {
      return false;
    }

    // Hide population if value is "0"
    if (itemTitle === 'population' && trimmedValue === '0') {
      return false;
    }

    // Check for common "no data" placeholder values (case-insensitive)
    const noDataPatterns = [
      'no data available',
      'no data',
      'n/a',
      'na',
      'not available',
      'unavailable',
      'data not available',
    ];

    const lowerValue = trimmedValue.toLowerCase();
    const isPlaceholder = noDataPatterns.some(
      pattern => lowerValue === pattern || lowerValue.includes(pattern)
    );

    return !isPlaceholder;
  });

  if (!itemsWithData || itemsWithData.length === 0) {
    return null;
  }

  // Create a map of items by title (case-insensitive)
  const itemsMap = new Map(
    itemsWithData.map(item => [item.title.toLowerCase(), item])
  );

  // Get items in preferred order
  const orderedItems = preferredOrder
    .map(key => itemsMap.get(key))
    .filter(Boolean); // Remove undefined items

  // Get items not in preferred order and append them
  const remainingItems = itemsWithData.filter(
    item => !preferredOrder.includes(item.title.toLowerCase())
  );

  // Combine ordered items with remaining items
  // Pass icon string if available, StatCard will use unified hook to resolve it
  const processedItems: StatItem[] = [...orderedItems, ...remainingItems].map(
    item => ({
      title: item.title,
      value: item.value,
      icon: item.icon || undefined, // Pass icon string if available
    })
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.5,
        delay: 0.15,
        ease: [0.4, 0, 0.2, 1],
        opacity: { duration: 0.4 },
      }}
      className={className}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: spacing[3],
      }}
    >
      <SectionHeader title='Destination Overview' />
      <div
        style={{
          display: 'grid',
          gap: spacing[2],
          padding: `0 ${spacing[3]}px ${spacing[3]}px`,
          width: '100%',
        }}
        className='grid grid-cols-2 md:grid-cols-3'
      >
        {processedItems.map((item, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.4,
              delay: 0.3 + index * 0.08,
              ease: [0.4, 0, 0.2, 1],
            }}
          >
            <StatCard title={item.title} value={item.value} icon={item.icon} />
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
};
